<script setup lang="ts">
/**
 * Контекст-меню строки — макет 354-2770; на телефоне — шторка в два уровня
 * (359-20139, 359-20259).
 *
 * Один компонент на обе платформы: уровень с детьми раскрывается заменой
 * списка с кнопкой «Назад» — и в меню, и в шторке. Боковое подменю макета
 * свёрнуто в это же поведение сознательно: одна механика вместо двух, и на
 * узком экране ей не тесно.
 *
 * У нижней кромки меню раскрывается вверх (flip по getBoundingClientRect):
 * иначе последние пункты уезжают за экран ровно у нижних строк списка.
 */
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { nextTick, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useIsMobile } from '@/composables/useIsMobile'

export interface MenuItem {
  key: string
  label: string
  children?: { key: string; label: string }[]
  danger?: boolean
}

defineProps<{ items: MenuItem[] }>()

const emit = defineEmits<{ select: [key: string, child?: string] }>()

const { t } = useI18n()
const isMobile = useIsMobile()

const isOpen = ref(false)
const x = ref(0)
const y = ref(0)
/** Раскрытый уровень 2: пункт, чьи дети сейчас на экране */
const level = ref<MenuItem | null>(null)
const panel = ref<HTMLElement | null>(null)

const open = async (clientX: number, clientY: number) => {
  level.value = null
  x.value = clientX
  y.value = clientY
  isOpen.value = true

  await nextTick()

  // Flip: у нижней кромки меню раскрывается вверх, у правой — влево.
  const box = panel.value?.getBoundingClientRect()

  if (!box) return

  if (box.bottom > window.innerHeight) y.value = Math.max(8, clientY - box.height)
  if (box.right > window.innerWidth) x.value = Math.max(8, clientX - box.width)
}

const close = () => {
  isOpen.value = false
  level.value = null
}

const pick = (item: MenuItem) => {
  if (item.children?.length) {
    level.value = item

    return
  }

  emit('select', item.key)
  close()
}

const pickChild = (child: { key: string }) => {
  if (!level.value) return

  emit('select', level.value.key, child.key)
  close()
}

// Слушатель глобальный: фокус в момент открытия остаётся на строке, и
// keydown на панели Escape бы не увидел.
const onKeydown = (event: KeyboardEvent) => {
  if (isOpen.value && event.key === 'Escape') close()
}

window.addEventListener('keydown', onKeydown)
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

defineExpose({ open, close })
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="cmenu"
      :class="{ 'cmenu--sheet': isMobile }"
      @click.self="close"
      @contextmenu.prevent
    >
      <div
        ref="panel"
        class="cmenu__panel"
        role="menu"
        :style="isMobile ? undefined : { left: `${x}px`, top: `${y}px` }"
      >
        <template v-if="!level">
          <button
            v-for="item in items"
            :key="item.key"
            type="button"
            class="cmenu__item"
            :class="{ 'cmenu__item--danger': item.danger }"
            role="menuitem"
            @click="pick(item)"
          >
            {{ item.label }}
            <ChevronRight v-if="item.children?.length" :size="14" />
          </button>
        </template>

        <template v-else>
          <button type="button" class="cmenu__item cmenu__back" @click="level = null">
            <ChevronLeft :size="14" />
            {{ t('menu.back') }}
          </button>

          <button
            v-for="child in level.children"
            :key="child.key"
            type="button"
            class="cmenu__item"
            role="menuitem"
            @click="pickChild(child)"
          >
            {{ child.label }}
          </button>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.cmenu {
  position: fixed;
  inset: 0;
  /* Ниже слоёв ДС (наложения пакета начинаются с 9999), выше контента. */
  z-index: 900;
}

.cmenu__panel {
  position: absolute;
  min-width: 220px;
  padding: 4px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.cmenu__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  cursor: pointer;
}

.cmenu__item:hover {
  background: var(--color-bg-muted);
}

.cmenu__item--danger {
  color: var(--color-danger);
}

.cmenu__back {
  justify-content: flex-start;
  color: var(--color-text-muted);
}

/* Шторка: тот же список, прижатый к низу, поверх таб-бара, с safe-area. */
.cmenu--sheet {
  display: flex;
  align-items: flex-end;
  background: rgb(0 0 0 / 40%);
}

.cmenu--sheet .cmenu__panel {
  position: static;
  width: 100%;
  min-width: 0;
  max-height: 70vh;
  padding: 8px 8px calc(8px + env(safe-area-inset-bottom));
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  overflow-y: auto;
}

.cmenu--sheet .cmenu__item {
  height: 44px;
  font-size: var(--font-size-body);
}
</style>
